/**
 * Route Optimizer Dashboard — Main app component.
 * Shows company list, branch list, quick-start buttons.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as api from './services/routeOptimizerService';
import './styles/RouteOptimizerApp.css';

export default function RouteOptimizerApp() {
  const { user } = useAuth();
  const canManageUsers = (user?.level ?? 3) <= 2;
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [quickStarting, setQuickStarting] = useState(null); // null or type string
  const [newBranchNames, setNewBranchNames] = useState({});
  const [creatingBranch, setCreatingBranch] = useState(null);
  const navigate = useNavigate();

  const loadCompanies = useCallback(async () => {
    try {
      const data = await api.listCompanies();
      setCompanies(data);
      // Load branches for each company
      const branchMap = {};
      for (const c of data) {
        branchMap[c.id] = await api.listBranches(c.id);
      }
      setBranches(branchMap);
    } catch (err) {
      console.error('Failed to load companies:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompanyName.trim()) return;
    setCreating(true);
    try {
      await api.createCompany(newCompanyName.trim());
      setNewCompanyName('');
      await loadCompanies();
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCompany = async (cid) => {
    if (!window.confirm('Delete this company and all its data?')) return;
    await api.deleteCompany(cid);
    await loadCompanies();
  };

  const handleQuickStart = async (type) => {
    setQuickStarting(type);
    try {
      let result;
      if (type === 'certus') {
        result = await api.quickStart();
      } else if (type === 'belgrade') {
        result = await api.quickStartBelgrade();
      } else {
        result = await api.quickStartMiami();
      }
      await loadCompanies();
      if (result.branch_id) {
        navigate(`/routeoptimizer/branch/${result.branch_id}`);
      }
    } catch (err) {
      console.error('Quick start failed:', err);
    } finally {
      setQuickStarting(null);
    }
  };

  const handleCreateBranch = async (e, companyId) => {
    e.preventDefault();
    const name = (newBranchNames[companyId] || '').trim();
    if (!name) return;
    setCreatingBranch(companyId);
    try {
      await api.createBranch(companyId, { name });
      setNewBranchNames(prev => ({ ...prev, [companyId]: '' }));
      await loadCompanies();
    } catch (err) {
      console.error('Failed to create branch:', err);
    } finally {
      setCreatingBranch(null);
    }
  };

  if (loading) {
    return <div className="ro-loading">Loading...</div>;
  }

  return (
    <div className="ro-dashboard">
      <div className="ro-header">
        <h1>Route Optimizer</h1>
        <p className="ro-subtitle">VRP route optimization & planning</p>
        {canManageUsers && (
          <button
            className="ro-btn ro-btn-sm ro-header-action"
            onClick={() => navigate('/routeoptimizer/admin')}
            aria-label="Open admin settings panel"
          >
            Admin Settings
          </button>
        )}
      </div>

      {/* Quick Start */}
      <div className="ro-card ro-quick-start">
        <h2>Quick Start</h2>
        <p>Load pre-computed demo data to explore the optimizer immediately.</p>
        <div className="ro-quick-buttons">
            <button
              className="ro-btn ro-btn-primary"
              onClick={() => handleQuickStart("certus")}
              disabled={quickStarting != null}
            >
              {quickStarting === "certus" ? "Setting up CERTUS..." : "CERTUS Oxford (4 sims)"}
            </button>
            <button
              className="ro-btn ro-btn-secondary"
              onClick={() => handleQuickStart("belgrade")}
              disabled={quickStarting != null}
            >
              {quickStarting === "belgrade" ? "Setting up Belgrade..." : "Belgrade (10 sims)"}
            </button>
            <button
              className="ro-btn ro-btn-secondary"
              onClick={() => handleQuickStart("miami")}
              disabled={quickStarting != null}
            >
              {quickStarting === "miami" ? "Setting up Miami Beach..." : "Miami Beach (10 sims)"}
            </button>
          </div>
      </div>

      {/* Setup Guide — only show when no companies exist */}
      {companies.length === 0 && (
        <div className="ro-card ro-setup-guide">
          <h2>Getting Started</h2>
          <div className="ro-steps">
            <div className="ro-step"><span className="ro-step-num">1</span><span>Create a <strong>Company</strong></span></div>
            <div className="ro-step"><span className="ro-step-num">2</span><span>Add a <strong>Branch</strong></span></div>
            <div className="ro-step"><span className="ro-step-num">3</span><span>Create a <strong>Weekly Setup</strong> (each week = one scenario)</span></div>
            <div className="ro-step"><span className="ro-step-num">4</span><span>Upload <strong>Addresses & Workers</strong> (or load demo data)</span></div>
            <div className="ro-step"><span className="ro-step-num">5</span><span>Click <strong>Optimize Routes</strong></span></div>
            <div className="ro-step"><span className="ro-step-num">6</span><span>View <strong>Interactive Route Map</strong></span></div>
          </div>
        </div>
      )}

      {/* Company List */}
      <div className="ro-card">
        <h2>Companies</h2>
        {companies.length === 0 ? (
          <p className="ro-empty">No companies yet. Use Quick Start or create one below.</p>
        ) : (
          <div className="ro-company-list">
            {companies.map(c => (
              <div key={c.id} className="ro-company-item">
                <div className="ro-company-info">
                  <h3
                    className="ro-company-name-link"
                    onClick={(e) => { e.stopPropagation(); navigate(`/routeoptimizer/company/${c.id}`); }}
                    style={{ cursor: 'pointer', color: 'var(--ro-blue)' }}
                  >
                    {c.name}
                  </h3>
                  <span className="ro-meta">{c.created_at}</span>
                </div>
                <div className="ro-company-branches-col">
                  <div className="ro-company-branches">
                    {(branches[c.id] || []).map(b => (
                      <div
                        key={b.id}
                        className="ro-branch-chip"
                        onClick={() => navigate(`/routeoptimizer/branch/${b.id}`)}
                      >
                        {b.name}
                        <span className="ro-badge">{b.week_count} weeks</span>
                      </div>
                    ))}
                  </div>
                  <form
                    className="ro-add-branch-form"
                    onSubmit={(e) => handleCreateBranch(e, c.id)}
                  >
                    <input
                      type="text"
                      value={newBranchNames[c.id] || ''}
                      onChange={e => setNewBranchNames(prev => ({ ...prev, [c.id]: e.target.value }))}
                      placeholder="New branch name"
                      className="ro-input ro-input-sm"
                    />
                    <button
                      type="submit"
                      className="ro-btn ro-btn-primary ro-btn-sm"
                      disabled={creatingBranch === c.id}
                    >
                      {creatingBranch === c.id ? 'Adding...' : 'Add Branch'}
                    </button>
                  </form>
                </div>
                <button
                  className="ro-btn ro-btn-danger ro-btn-sm"
                  onClick={() => handleDeleteCompany(c.id)}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create Company Form */}
        <form onSubmit={handleCreateCompany} className="ro-create-form">
          <input
            type="text"
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            placeholder="New company name"
            className="ro-input"
            disabled={creating}
          />
          <button type="submit" className="ro-btn ro-btn-primary" disabled={creating}>
            {creating ? 'Creating...' : 'Create Company'}
          </button>
        </form>
      </div>
    </div>
  );
}




